/* eslint-disable no-constant-condition */
/* eslint-disable no-param-reassign */
import { AxiosError, AxiosResponse, isAxiosError } from 'axios';
import qs from 'qs';
import { addMinutes, format, isEqual } from 'date-fns';
import { deleteObject, ref } from 'firebase/storage';
import {
  Appointments,
  AppointmentStatus,
  DepositPaymentStatus,
  Discounts,
  ItemViews,
  Payments,
} from '@prisma/client';
import API_URL from '../apiconfig';
import {
  Appointment,
  AppointmentWithCustomerAndEmployee,
  CheckoutFailureData,
  GeneralRequest,
  LoadedUser,
} from '../interfaces';
import { RejectValues } from '../utils/form/rejectForm';
import {
  CreateAptValues,
  ImageItem,
  removePersistedFiles,
} from '../utils/form/appointmentForm';
import { updateCustomer } from './user';
import { auth, storage } from '../firebase';
import client from '../utils/react-query/client';
import {
  AcceptFormState,
  AppointmentDepositCheckoutSession,
  CheckoutFormValues,
  MarkDepositPaidPayload,
  SortableKeys,
  TransferAppointmentDepositPayload,
} from '../types/appointments';
import { PaymentWithTransfersOrders } from '../types/payments';
import axios from './api';
import { cleanupFirebaseURL, removeWhitespace } from '../utils/global/helper';
import { getWalkInStartDateTime } from '../helpers/appointments';
import { uploadFiles } from './global';
import { SortValues } from '../types/ui';
import { isDiscountAllowed, isTipAllowed } from '../helpers/payments';
import { Conversation } from '../types/chats';

async function fetchGeneralRequests(
  businessId: string,
  cursor?: string,
  sort?: SortValues<SortableKeys>,
  deletedOnly?: boolean,
  excludeDeleted?: boolean,
  employeeIds?: string[],
): Promise<GeneralRequest[]> {
  const allResults: GeneralRequest[] = [];
  let currentCursor = cursor;
  let hasMore = true;

  try {
    while (hasMore) {
      const response = await axios.get(
        `${API_URL}/v1/appointments/general-requests`,
        {
          params: {
            limit: 25,
            businessId,
            cursor: currentCursor,
            sortOrder: sort?.order,
            sortOrderBy: sort?.orderBy,
            status: deletedOnly ? 'ARCHIVED' : undefined,
            excludeDeleted,
            employeeIds,
          },
          paramsSerializer: params =>
            qs.stringify(params, {
              arrayFormat: 'repeat',
              allowEmptyArrays: true,
            }),
        },
      );

      const { meta, result } = response.data;

      if (!result.length) {
        break;
      }

      allResults.push(...result);

      currentCursor = meta.cursor;
      hasMore = meta.hasMore;
    }

    return allResults;
  } catch (error) {
    throw new Error('Cannot get general requests');
  }
}

export async function getGeneralRequests({
  businessId,
  sort,
  deletedOnly,
  excludeDeleted,
  employeeIds,
}: {
  businessId: string;
  sort?: SortValues<SortableKeys>;
  deletedOnly?: boolean;
  employeeIds?: string[];
  excludeDeleted?: boolean;
}) {
  const appointments = await fetchGeneralRequests(
    businessId,
    undefined,
    sort,
    deletedOnly,
    excludeDeleted,
    employeeIds,
  );
  return appointments;
}

async function fetchAppointments(
  businessId: string,
  status: 'ACCEPTED' | 'REJECTED' | 'REQUESTED' | 'ARCHIVED',
  cursor?: string,
  sort?: SortValues<SortableKeys>,
  employeeIds?: string[],
): Promise<Appointment[]> {
  const allResults: Appointment[] = [];
  let currentCursor = cursor;
  let hasMore = true;

  try {
    while (hasMore) {
      const response = await axios.get(`${API_URL}/v1/appointments`, {
        params: {
          limit: 25,
          businessId,
          status: [status],
          cursor: currentCursor,
          sortOrder: sort?.order,
          sortOrderBy: sort?.orderBy,
          employeeIds,
        },
        paramsSerializer: params =>
          qs.stringify(params, {
            arrayFormat: 'repeat',
            allowEmptyArrays: true,
          }),
      });

      const { meta, result } = response.data;

      if (!result.length) {
        break;
      }

      allResults.push(...result);

      currentCursor = meta.cursor;
      hasMore = meta.hasMore;
    }

    return allResults;
  } catch (error) {
    throw new Error('Cannot get appointments');
  }
}

export function getCustomerUpdateError(err: any) {
  const errs = ['Phone number is already in use'];
  if (!('response' in err)) return 'Error updating customer detail';
  const errMsg = ((err as AxiosError)?.response?.data as any)?.message;
  // filter only the error message that is related to customer update
  if (errs.includes(errMsg)) return errMsg;
  return 'Error updating customer detail';
}

export async function getBookingRequests({
  businessId,
  status,
  sort,
  employeeIds,
}: {
  businessId: string;
  status: 'ACCEPTED' | 'REJECTED' | 'REQUESTED' | 'ARCHIVED';
  sort?: SortValues<SortableKeys>;
  employeeIds?: string[];
}) {
  const appointments = await fetchAppointments(
    businessId,
    status,
    undefined,
    sort,
    employeeIds,
  );
  return appointments;
}

export async function getAppointments({
  businessId,
  startDate,
  endDate,
  includeCanceled = true,
  limit,
  isReversed,
  onlyIncomplete,
  useExactDates,
  customerId,
  employeeId,
}: {
  businessId: string;
  startDate: Date | string;
  endDate: Date | string;
  includeCanceled?: boolean;
  limit?: number;
  isReversed?: boolean;
  onlyIncomplete?: boolean;
  useExactDates?: boolean;
  customerId?: string;
  employeeId?: string;
}) {
  const sDate =
    typeof startDate === 'string' ? startDate : format(startDate, 'yyyy-MM-dd');
  const eDate =
    typeof endDate === 'string' ? endDate : format(endDate, 'yyyy-MM-dd');

  try {
    const appointments = await axios.get(
      `${API_URL}/v1/appointments/list/${sDate}`,
      {
        params: {
          endDate: eDate,
          businessId,
          includeCanceled,
          limit,
          isReversed,
          onlyIncomplete,
          useExactDates,
          customerId,
          employeeId,
        },
      },
    );

    return appointments.data.result;
  } catch (error) {
    throw new Error('Cannot get appointments');
  }
}

export async function acceptAppointment(
  appointment: Appointment,
  values: AcceptFormState,
) {
  const lengthInt = parseInt(String(values.appointmentLengthInMinutes), 10);

  try {
    const appointmentResponse = await axios.patch(
      `${API_URL}/v1/appointments/${appointment.appointmentId}`,
      {
        status: 'ACCEPTED',
        employeeId: values.artist,
        appointmentLengthInMinutes: lengthInt,
        depositPrice: values.depositAmount,
        priceType: values.priceType,
        minPrice: values.minPrice,
        maxPrice: values.maxPrice,
        price: values.setPrice,
        hourlyPrice: values.hourlyPrice,
        customDesignMessage: values.customDesignMessage,
        accept: {
          numberOfSessions: values.numberOfSessions,
          schedulingType: values.schedulingType,
          bufferDays: values.bufferDays,
          depositPlacement: values.depositPlacement,
        },
      },
      {
        params: {
          businessId: appointment.businessId,
        },
      },
    );

    return appointmentResponse;
  } catch (error) {
    throw new Error('Cannot accept appointment');
  }
}

export async function acceptGeneralRequest(
  appointment: GeneralRequest,
  values: AcceptFormState,
) {
  const lengthInt = parseInt(String(values.appointmentLengthInMinutes), 10);

  try {
    const appointmentResponse = await axios.patch(
      `${API_URL}/v1/appointments/general-request/${appointment.requestId}`,
      {
        status: 'ACCEPTED',
        employeeId: values.artist,
        appointmentLengthInMinutes: lengthInt,
        depositPrice: values.depositAmount,
        priceType: values.priceType,
        minPrice: values.minPrice,
        maxPrice: values.maxPrice,
        price: values.setPrice,
        hourlyPrice: values.hourlyPrice,
        customDesignMessage: values.customDesignMessage,
        accept: {
          numberOfSessions: values.numberOfSessions,
          schedulingType: values.schedulingType,
          bufferDays: values.bufferDays,
          depositPlacement: values.depositPlacement,
        },
      },
      {
        params: {
          businessId: appointment.businessId,
        },
      },
    );

    return appointmentResponse;
  } catch (error) {
    throw new Error('Cannot accept appointment');
  }
}

export async function rejectAppointment(
  appointment: Appointment,
  values: RejectValues,
) {
  try {
    const appointmentResponse = await axios.patch(
      `${API_URL}/v1/appointments/${appointment.appointmentId}`,
      {
        status: 'REJECTED',
        rejectionMessage: values.rejectionMessage,
      },
      {
        params: {
          businessId: appointment.businessId,
        },
      },
    );

    return appointmentResponse;
  } catch (error) {
    throw new Error('Cannot reject appointment');
  }
}

export async function rejectGeneralRequest(
  appointment: GeneralRequest,
  values: RejectValues,
  loadedUserId: string,
) {
  try {
    const appointmentResponse = await axios.patch(
      `${API_URL}/v1/appointments/general-request/${appointment.requestId}`,
      {
        status: 'REJECTED',
        employeeId: loadedUserId,
        rejectionMessage: values.rejectionMessage,
      },
      {
        params: {
          businessId: appointment.businessId,
        },
      },
    );

    return appointmentResponse;
  } catch (error) {
    throw new Error('Cannot reject appointment');
  }
}

export async function initiateCheckout(
  appointment: Appointment,
  values: CheckoutFormValues,
  failureData: CheckoutFailureData | null,
  selectedDiscount?: Discounts | null,
): Promise<
  Appointment & {
    paymentIntent?: {
      id: string;
      client_secret: string;
    };
  }
> {
  // Patch update to customers
  await updateCustomer(
    {
      firstName: values.customer.firstName,
      lastName: values.customer.lastName,
      phoneNumber: values.customer.phoneNumber,
      email: values.customer.email,
      dob: values.customer.dob,
    },
    values.customer.customerId,
  ).catch(err => {
    throw new Error(getCustomerUpdateError(err));
  });

  const data: any = {
    price: values.price,
    businessId: appointment.businessId,
    paymentMethod: values.paymentMethod,
    includeBonus: values.includeBonus,
    paymentIntentId:
      values.paymentIntentId || failureData?.paymentIntentId || undefined,
    isCashTaxed: values.isCashTaxed,
  };

  if (values.paymentMethod === 'MANUAL') {
    data.paymentMethod = 'CREDIT';
    data.isManualCheckout = true;
  }

  if (values.paymentMethod === 'FREE') {
    data.isNoCharge = true;
    data.paymentMethod = 'CASH';
  }

  if (isTipAllowed(values.paymentMethod) && values.tipAmt) {
    data.tipAmt = values.tipAmt;
  }

  if (isDiscountAllowed(values.paymentMethod) && selectedDiscount) {
    data.discountId = selectedDiscount.id;
  }

  const appointmentResponse = await axios.patch(
    `${API_URL}/v1/appointments/checkout/${appointment.appointmentId}`,
    data,
  );

  return appointmentResponse?.data?.result;
}

export async function markAppointmentNoShow(
  appointmentId: string,
  customer?: CheckoutFormValues['customer'],
) {
  try {
    if (customer) {
      await updateCustomer(
        {
          firstName: customer.firstName,
          lastName: customer.lastName,
          phoneNumber: customer.phoneNumber,
          email: customer.email,
          dob: customer.dob,
        },
        customer.customerId,
      ).catch(err => {
        throw new Error(getCustomerUpdateError(err));
      });
    }

    const appointmentResponse = await axios.patch(
      `${API_URL}/v1/appointments/mark-no-show/${appointmentId}`,
    );

    return appointmentResponse?.data?.result as Appointments;
  } catch (error) {
    throw new Error('Cannot mark appointment as no show');
  }
}

export async function closeCheckout(
  appointmentId: string,
  payload: { tattooPrice: number; tipAmt: number; paymentId?: string },
) {
  const appointmentResponse = await axios.patch(
    `${API_URL}/v1/appointments/complete-checkout/${appointmentId}`,
    payload,
  );

  return appointmentResponse;
}

export async function closeOrderCheckout(orderId: string) {
  const appointmentResponse = await axios.patch(
    `${API_URL}/v1/orders/${orderId}/complete-checkout`,
  );

  return appointmentResponse?.data?.result;
}

export async function uploadApptImages(images: ImageItem[]) {
  const user = auth.currentUser;
  const authServiceId = user?.uid;
  if (!authServiceId) throw new Error('Not authenticated');

  const imgUrls = await uploadFiles(
    images.map((img, i) => ({
      id: img.id,
      url: img.url,
      path: `users/${authServiceId}/appointment_${Date.now()}_${i}`,
    })),
    {
      onSuccess: img => {
        removePersistedFiles([img.id]);
      },
    },
  );

  return imgUrls;
}

export async function deleteApptImages(
  existingImgsId: string[],
  imagesToDelete: string[],
) {
  const promises: Promise<void>[] = [];
  imagesToDelete.forEach(imgId => {
    const imgRef = ref(storage, cleanupFirebaseURL(imgId));
    promises.push(deleteObject(imgRef));
  });
  try {
    await Promise.all(promises);
  } catch (error) {
    // custom msg
    throw new Error('Error deleting images');
  }

  const newRefImgsId = existingImgsId?.filter(
    imgId => !imagesToDelete?.includes(imgId),
  );

  return newRefImgsId;
}

export async function createAppointment(appointment: CreateAptValues): Promise<
  AxiosResponse<{
    result: Pick<Appointment, 'appointmentId' | 'customerId'>;
  }>
> {
  let customerId = appointment.customerId || '';

  const apptPhoneNumber = !appointment.phoneNumber.startsWith('+')
    ? `+${appointment.phoneNumber}`
    : appointment.phoneNumber;

  if (!appointment.customerId) {
    const createCustomerPayload: any = {
      firstName: appointment.firstName,
      lastName: appointment.lastName,
      phoneNumber: apptPhoneNumber,
    };

    if (appointment.email) {
      createCustomerPayload.email = appointment.email;
    }

    if (appointment.dob) {
      createCustomerPayload.dob = appointment.dob;
    }

    const createCustomerResponse = await axios
      .post(`${API_URL}/v1/customers`, createCustomerPayload)
      .catch(error => {
        throw new Error(error.response.data.message);
      });

    customerId = createCustomerResponse.data.result.customerId;
  } else {
    // Patch update to customers
    await updateCustomer(
      {
        firstName: appointment.firstName,
        lastName: appointment.lastName,
        phoneNumber: apptPhoneNumber,
        email: appointment.email,
        dob: appointment.dob,
      },
      appointment.customerId,
    ).catch(err => {
      throw new Error(getCustomerUpdateError(err));
    });
  }

  appointment.customerId = customerId;

  if (appointment.appointmentType === 'WALK_IN') {
    if (!appointment.appointmentLengthInMinutes) {
      appointment.appointmentLengthInMinutes = '60';
    }
    const tempStartDate = getWalkInStartDateTime();
    const tempEndDate = addMinutes(
      tempStartDate,
      parseInt(appointment.appointmentLengthInMinutes!, 10),
    );

    appointment.startDateTime = tempStartDate.toISOString();
    appointment.endDateTime = tempEndDate.toISOString();
  } else if (!appointment.isBookingLink) {
    const tempStartDate = new Date(appointment.startDateTime);
    const tempEndDate = addMinutes(
      tempStartDate,
      parseInt(appointment.appointmentLengthInMinutes!, 10),
    );

    appointment.startDateTime = tempStartDate.toISOString();
    appointment.endDateTime = tempEndDate.toISOString();
  }

  let serviceId;
  let piercingLocationId;
  let url = `${API_URL}/v1/appointments`;
  if (!appointment.serviceId || appointment.serviceId === '0') {
    serviceId = null;
    piercingLocationId = null;
  } else {
    serviceId = parseInt(appointment.serviceId, 10);
    piercingLocationId = appointment.piercingLocationId
      ? parseInt(appointment.piercingLocationId, 10)
      : null;
    url = `${API_URL}/v1/appointments/piercing`;
  }

  delete appointment.dob;

  // upload images
  const newImages = appointment.newImages || [];
  if (newImages.length) {
    const refImgsId = await uploadApptImages(newImages);
    if (refImgsId?.length) appointment.refImgsId = refImgsId;
  }
  delete appointment.newImages;

  const appointmentLengthInMinutes = parseInt(
    appointment.appointmentLengthInMinutes!,
    10,
  );

  const payload = {
    ...appointment,
    depositPlacement: appointment.depositPlacement || undefined,
    additionalAppointments: (appointment.additionalAppointments || []).map(
      appt => ({
        ...appt,
        appointmentLengthInMinutes: appt.appointmentLengthInMinutes
          ? parseInt(appt.appointmentLengthInMinutes, 10)
          : appointmentLengthInMinutes,
      }),
    ),
    appointmentLengthInMinutes,
    serviceId,
    piercingLocationId,
    status: 'SCHEDULED',
    businessTripId: appointment.businessTripId || null,
  };

  if (payload.isBookingLink) {
    payload.status = 'ACCEPTED';
    // @ts-expect-error
    payload.startDateTime = undefined;
    // @ts-expect-error
    payload.endDateTime = undefined;
    if (payload.appointmentType === 'CONSULTATION') {
      payload.priceType = 'FIXED';
      payload.price = 0;
      payload.depositPrice = 0;
      payload.requestWaiver = false;
    }
  }

  const appointmentResponse = await axios.post(url, payload).catch(error => {
    throw new Error(error.response.data.message);
  });
  return appointmentResponse as AxiosResponse<{
    result: Pick<Appointment, 'appointmentId' | 'customerId'>;
  }>;
}

export async function updateAppointment(
  appointment: CreateAptValues,
  existingAppointment: Appointment,
): Promise<
  AxiosResponse<{
    result: Appointment;
  }>
> {
  const newAptDate = new Date(appointment.startDateTime);
  const oldAptDate = new Date(existingAppointment.startDateTime!);

  delete appointment.additionalAppointments;

  let { status } = existingAppointment;
  const isBeingRescheduled =
    !isEqual(newAptDate, oldAptDate) ||
    String(appointment.appointmentLengthInMinutes) !==
      String(existingAppointment.appointmentLengthInMinutes) ||
    String(removeWhitespace(appointment.employeeId)) !==
      String(removeWhitespace(existingAppointment.employeeId));

  if (isBeingRescheduled) {
    status = 'RESCHEDULED';
  }

  const tempStartDate = new Date(appointment.startDateTime);
  const tempEndDate = addMinutes(
    tempStartDate,
    parseInt(appointment.appointmentLengthInMinutes!, 10),
  );

  appointment.startDateTime = tempStartDate.toISOString();
  appointment.endDateTime = tempEndDate.toISOString();

  let serviceId;
  let piercingLocationId;
  if (!appointment.serviceId || appointment.serviceId === '0') {
    serviceId = null;
    piercingLocationId = null;
  } else {
    serviceId = parseInt(appointment.serviceId, 10);
    piercingLocationId = appointment.piercingLocationId
      ? parseInt(appointment.piercingLocationId, 10)
      : null;
  }

  // Patch update to customers
  await updateCustomer(
    {
      firstName: appointment.firstName,
      lastName: appointment.lastName,
      phoneNumber: appointment.phoneNumber,
      email: appointment.email,
      dob: appointment.dob,
    },
    existingAppointment.customerId,
  ).catch(err => {
    throw new Error(getCustomerUpdateError(err));
  });

  // upload new images
  appointment.refImgsId = existingAppointment.refImgsId;
  if (appointment.newImages?.length) {
    const refImgsId = await uploadApptImages(appointment.newImages);
    if (refImgsId)
      appointment.refImgsId = [...(appointment.refImgsId ?? []), ...refImgsId];
  }
  delete appointment.newImages;

  if (appointment.refImgsId && appointment.imagesToDelete?.length) {
    const newRefImgsId = await deleteApptImages(
      appointment.refImgsId,
      appointment.imagesToDelete,
    );
    appointment.refImgsId = newRefImgsId;
  }
  delete appointment.imagesToDelete;

  const appointmentLengthInMinutes = parseInt(
    appointment.appointmentLengthInMinutes!,
    10,
  );

  const appointmentResponse = await axios
    .patch(
      `${API_URL}/v1/appointments/${existingAppointment.appointmentId}`,
      {
        ...appointment,
        appointmentLengthInMinutes,
        serviceId,
        piercingLocationId,
        status,
        businessTripId: appointment.businessTripId || null,
      },
      {
        params: {
          businessId: existingAppointment.businessId,
        },
      },
    )
    .catch(error => {
      throw new Error(error.response.data.message);
    });
  return appointmentResponse;
}

export async function scheduleRequest(
  appointment: CreateAptValues,
  existingRequest: Appointment | GeneralRequest,
): Promise<AxiosResponse> {
  appointment.customerId = existingRequest.customerId;
  const isGeneralRequest = !!(existingRequest as GeneralRequest).requestId;

  // Patch update to customers
  await updateCustomer(
    {
      firstName: appointment.firstName,
      lastName: appointment.lastName,
      phoneNumber: appointment.phoneNumber,
      email: appointment.email,
      dob: appointment.dob,
    },
    appointment.customerId,
  ).catch(err => {
    throw new Error(getCustomerUpdateError(err));
  });

  const tempStartDate = new Date(appointment.startDateTime);
  const tempEndDate = addMinutes(
    tempStartDate,
    parseInt(appointment.appointmentLengthInMinutes!, 10),
  );

  appointment.startDateTime = tempStartDate.toISOString();
  appointment.endDateTime = tempEndDate.toISOString();

  let serviceId;
  let piercingLocationId;
  if (!appointment.serviceId || appointment.serviceId === '0') {
    serviceId = null;
    piercingLocationId = null;
  } else {
    serviceId = parseInt(appointment.serviceId, 10);
    piercingLocationId = appointment.piercingLocationId
      ? parseInt(appointment.piercingLocationId, 10)
      : null;
  }

  delete appointment.dob;

  // upload new images
  appointment.refImgsId = existingRequest.refImgsId;
  if (appointment.newImages?.length) {
    const refImgsId = await uploadApptImages(appointment.newImages);
    if (refImgsId)
      appointment.refImgsId = [...(appointment.refImgsId ?? []), ...refImgsId];
  }

  // delete images
  if (appointment.refImgsId && appointment.imagesToDelete?.length) {
    const newRefImgsId = await deleteApptImages(
      appointment.refImgsId,
      appointment.imagesToDelete,
    );
    appointment.refImgsId = newRefImgsId;
  }

  const appointmentLengthInMinutes = parseInt(
    appointment.appointmentLengthInMinutes!,
    10,
  );
  const additionalAppointments = (appointment.additionalAppointments || []).map(
    appt => ({
      ...appt,
      appointmentLengthInMinutes: appt.appointmentLengthInMinutes
        ? parseInt(appt.appointmentLengthInMinutes, 10)
        : appointmentLengthInMinutes,
    }),
  );

  if (isGeneralRequest) {
    const appointmentResponse = await axios
      .patch(
        `${API_URL}/v1/appointments/general-request/${
          (existingRequest as GeneralRequest).requestId
        }`,
        {
          ...appointment,
          appointmentLengthInMinutes,
          additionalAppointments,
          serviceId,
          piercingLocationId,
          status: 'SCHEDULED',
          businessTripId: appointment.businessTripId || null,
        },
        {
          params: {
            businessId: existingRequest.businessId,
          },
        },
      )
      .catch(error => {
        throw new Error(error.response.data.message);
      });
    return appointmentResponse;
  }

  const appointmentResponse = await axios
    .patch(
      `${API_URL}/v1/appointments/${
        (existingRequest as Appointment).appointmentId
      }`,
      {
        ...appointment,
        appointmentLengthInMinutes,
        additionalAppointments,
        serviceId,
        piercingLocationId,
        status: 'SCHEDULED',
        businessTripId: appointment.businessTripId || null,
      },
      {
        params: {
          businessId: existingRequest.businessId,
        },
      },
    )
    .catch(error => {
      throw new Error(error.response.data.message);
    });
  return appointmentResponse;
}

export async function getGeneralRequest(requestId: string | undefined) {
  try {
    const result = await axios.get(
      `${API_URL}/v1/appointments/general-requests/${requestId}`,
    );
    return result.data.result;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('Unknown error occurred');
    }
  }
}

export async function getAppointmentById(appointmentId: string | undefined) {
  try {
    const appointments = await axios.get(
      `${API_URL}/v1/appointments/${appointmentId}`,
    );
    return appointments.data.result;
  } catch (error: unknown) {
    // Check if the error is an Axios error with a response and a 404 status
    if (
      isAxiosError(error) &&
      error.response &&
      error.response.status === 404
    ) {
      return await getGeneralRequest(appointmentId);
    }
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('Unknown error occurred');
    }
  }
}

export async function getAppointmentTransactions(
  appointmentId: string | undefined,
) {
  try {
    const appointments = await axios.get(
      `${API_URL}/v1/appointments/transactions/${appointmentId}/v2`,
    );
    return appointments.data.result as PaymentWithTransfersOrders[];
  } catch (error: unknown) {
    // Check if the error is an Axios error with a response and a 404 status
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('Unknown error occurred');
    }
  }
}

export async function cancelAppointment(
  existingAppointment: Appointment,
  { depositStatus }: { depositStatus?: DepositPaymentStatus } = {},
): Promise<AxiosResponse> {
  const status = 'CANCELED';

  const appointmentResponse = await axios
    .patch(
      `${API_URL}/v1/appointments/${existingAppointment.appointmentId}`,
      {
        status,
        depositStatus,
      },
      {
        params: {
          businessId: existingAppointment.businessId,
        },
      },
    )
    .catch(error => {
      throw new Error(error.response.data.message);
    });
  return appointmentResponse;
}

export async function archiveAppointment(
  existingAppointment: Appointment | GeneralRequest,
): Promise<AxiosResponse> {
  const status = 'ARCHIVED';

  const appointmentResponse = await axios
    .patch(
      `${API_URL}/v1/appointments/archive/${
        (existingAppointment as Appointment).appointmentId ||
        (existingAppointment as GeneralRequest).requestId
      }`,
      { status },
      {
        params: {
          businessId: existingAppointment.businessId,
        },
      },
    )
    .catch(error => {
      throw new Error(error.response.data.message);
    });
  return appointmentResponse;
}

export async function createAppointmentChat(
  appointment:
    | Pick<Appointment, 'appointmentId'>
    | Pick<GeneralRequest, 'requestId'>,
  loadedUser: LoadedUser,
) {
  let payload;

  if ((appointment as Appointment).appointmentId) {
    payload = {
      appointmentId: (appointment as Appointment).appointmentId,
      businessId: loadedUser.business.id,
    };
  } else if ((appointment as GeneralRequest).requestId) {
    payload = {
      generalRequestId: (appointment as GeneralRequest).requestId,
      businessId: loadedUser.business.id,
    };
  } else {
    throw new Error('Invalid appointment type');
  }

  try {
    const chatResponse = await axios.post(
      `${API_URL}/v1/appointments/chat`,
      payload,
    );
    return {
      conversation: chatResponse?.data?.result as Conversation,
      appointmentId: chatResponse?.data?.meta?.appointmentId,
      generalRequestId: chatResponse?.data?.meta?.generalRequestId,
    };
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Unable to load chat: ${error.message}`);
    }
    // If it's not an Error instance, you might want to handle it differently
    throw new Error('An unknown error occurred');
  }
}

export async function sendAppointmentReminder(
  appointment: Appointment | string, // string is the appointmentId
) {
  try {
    if (!appointment) {
      throw new Error('No appointment provided');
    }
    const appointmentId =
      typeof appointment === 'string' ? appointment : appointment.appointmentId;
    const response = await axios.post(
      `${API_URL}/v1/appointments/${appointmentId}/remind`,
      null,
    );

    return response;
  } catch (error) {
    throw new Error('Cannot resend booking link');
  }
}

export async function resendCheckoutLink(
  appointment: Appointment | string, // string is the appointmentId
) {
  try {
    if (!appointment) {
      throw new Error('No appointment provided');
    }
    const appointmentId =
      typeof appointment === 'string' ? appointment : appointment.appointmentId;
    const response = await axios.post(
      `${API_URL}/v1/appointments/${appointmentId}/resend-checkout-link`,
      null,
    );

    return response;
  } catch (error) {
    throw new Error('Cannot resend booking link');
  }
}

export async function invalidateAppointment(appointmentId: string) {
  await client.invalidateQueries({ queryKey: ['appointment', appointmentId] });
}

export async function invalidateAppointments() {
  await client.invalidateQueries({ queryKey: ['appointments'] });
}

export async function markDepositPaid(
  appointmentId: string,
  { depositPaymentMethod }: MarkDepositPaidPayload,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/appointments/mark-deposit-paid/${appointmentId}`,
      {
        depositPaymentMethod,
      },
    );
    return response;
  } catch (error) {
    throw new Error('Cannot mark deposit as paid');
  }
}

export async function invalidateAppointmentPayments(appointmentId: string) {
  await client.invalidateQueries({
    queryKey: ['appointment-payments', appointmentId],
    exact: false,
    type: 'active',
  });
}

export async function invalidateAvailability() {
  await Promise.all([
    client.invalidateQueries({
      queryKey: ['dayAvailableTimes'],
      exact: false,
    }),
    client.invalidateQueries({
      queryKey: ['monthAvailability'],
      exact: false,
    }),
  ]);
}

export async function getUnreadRequests<
  TIncludeGeneralRequests extends boolean = true,
>(
  businessId?: string,
  includeGeneralRequests: TIncludeGeneralRequests = true as TIncludeGeneralRequests,
) {
  try {
    const response = await axios.get(`${API_URL}/v1/appointments/unreads`, {
      params: {
        businessId,
        includeGeneralRequests,
      },
    });
    return response.data.result as TIncludeGeneralRequests extends true
      ? {
          generalRequests: string[];
          appointments: string[];
        }
      : string[];
  } catch (error) {
    throw new Error('Cannot get unread requests');
  }
}

export async function markRequestAsRead(
  requestId: string,
  type: 'appointment' | 'generalRequest',
) {
  try {
    const response = await axios.post(
      `${API_URL}/v1/appointments/mark-read/${requestId}`,
      {
        type,
      },
    );
    return response.data.result as ItemViews;
  } catch (error) {
    throw new Error('Cannot mark request as read');
  }
}

export async function getAppointmentsForDepositTransfer({
  businessId,
  customerId,
}: {
  businessId: string;
  customerId: string;
}) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/appointments/deposit-transfer`,
      {
        params: {
          businessId,
          customerId,
        },
      },
    );

    return response?.data?.result as {
      from: AppointmentWithCustomerAndEmployee[];
      to: AppointmentWithCustomerAndEmployee[];
    };
  } catch (error) {
    throw new Error('Cannot get appointments for deposit transfer');
  }
}

export async function transferAppointmentDeposit(
  payload: TransferAppointmentDepositPayload,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/appointments/deposit-transfer`,
      payload,
    );

    return response?.data?.result as {
      payment: Payments;
      fromAppointment: Appointments;
      toAppointment: Appointments;
    };
  } catch (error) {
    throw new Error('Cannot transfer appointment deposit');
  }
}

export async function getAppointmentDepositCheckoutSession({
  appointmentId,
  status,
  isPriceOnly,
  depositPrice,
  isTerminal,
}: {
  appointmentId: string;
  status: 'schedule' | 'reschedule';
  isPriceOnly?: boolean;
  depositPrice?: number;
  isTerminal?: boolean;
}) {
  try {
    const params = {
      appointmentId,
      status,
      isPriceOnly,
      depositPrice,
      isTerminal,
    };
    const response = await axios.get(`${API_URL}/v1/payment_intent/deposit`, {
      params,
    });

    return response.data.result as AppointmentDepositCheckoutSession;
  } catch (error) {
    throw new Error('Cannot get deposit checkout session');
  }
}

export async function cancelDepositCheckout(appointmentId: string) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/appointments/${appointmentId}/cancel-deposit-checkout`,
    );
    return response.data.result as Pick<Appointments, 'appointmentId'>;
  } catch (error) {
    throw new Error('Cannot cancel deposit checkout');
  }
}

export async function invalidateUnreadRequests() {
  await client.invalidateQueries({
    queryKey: ['unread-requests'],
    exact: false,
  });
}

export async function invalidateRequests(status?: AppointmentStatus) {
  const queryKey: any[] = ['requests'];
  if (status) queryKey.push({ status });
  await client.invalidateQueries({
    queryKey,
    exact: false,
  });
}

export async function invalidateDepositTransferAppointments() {
  await client.invalidateQueries({
    queryKey: ['deposit-transfer-appointments'],
    exact: false,
  });
}
